
import React, { useCallback, useEffect, useState } from 'react';

import { useSelector } from 'react-redux';

import useStyles from './useStyles';
import selectSessionToken from '../../selectors/sessionToken';

interface FolderItem {
  name: string;
  type: 'file' | 'directory';
  path: string;
  url?: string;
}

interface FolderViewerProps {
  folderPath: string;
  onClose: () => void;
  onNavigateToFile?: (filePath: string, fileName: string) => void;
  onNavigateToFolder?: (folderPath: string) => void;
}
//This component displays the contents of a folder in a modal view.
const FolderViewer: React.FC<FolderViewerProps> = ({
  folderPath,
  onClose,
  onNavigateToFile,
  onNavigateToFolder,
}) => {
  const classes = useStyles();
  const sessionToken = useSelector(selectSessionToken);

  const [items, setItems] = useState<FolderItem[]>([]);
  const [currentPath, setCurrentPath] = useState<string>('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  // This function returns an icon based on the item type (file or directory).
  const getFileIcon = (itemType: 'file' | 'directory') =>
    itemType === 'directory' ? '📁' : '📄';
  //This function handles clicks on folder items, navigating to files or folders as appropriate.
  const handleItemClick = useCallback(
    (item: FolderItem) => {
      if (item.type === 'directory') {
        onNavigateToFolder?.(item.path);
      } else {
        onNavigateToFile?.(item.path, item.name);
      }
    },
    [onNavigateToFile, onNavigateToFolder]
  );

  useEffect(() => {
    // Prevent body scroll while modal is open
    document.body.style.overflow = 'hidden';

    setLoading(true);
    setError('');
    setItems([]);
    setCurrentPath('');

    if (!folderPath || !sessionToken) {
      setError('Missing folder path or authentication token');
      setLoading(false);
      return () => {
        document.body.style.overflow = '';
      };
    }

    const params = new URLSearchParams({ path: folderPath });

    fetch(`/api/files?${params.toString()}`, {
      headers: { Authorization: `Bearer ${sessionToken}` },
    })
      .then(async (res) => {
        if (res.status === 401) {
          setError('Unauthorized');
          setLoading(false);
          return null;
        }
        if (!res.ok) {
          throw new Error(`Failed to fetch folder: ${res.status} ${res.statusText}`);
        }
        const data = await res.json();
        setItems(Array.isArray(data.items) ? data.items : []);
        setCurrentPath(data.currentPath || folderPath);
        setLoading(false);
        return null;
      })
      .catch((err) => {
        console.error('Error fetching folder:', err);
        setError('Error fetching folder contents.');
        setLoading(false);
      });

    return () => {
      // Restore body scroll on unmount
      document.body.style.overflow = '';
    };
  }, [folderPath, sessionToken]);

  return (
    <div className={classes.modalOverlay}>
      {/* Header */}
      <div className={classes.header}>
        <h3 className={classes.title}>
          {(currentPath || folderPath || 'Folder')}
        </h3>
        <div>
          <button onClick={onClose} className={classes.closeButton}>
            Close
          </button>
        </div>
      </div>

      {/* Content */}
      <div
        className={classes.folderContent}
        role="region"
        aria-label={`Folder ${currentPath || folderPath || ''}`}
      >
        {loading && (
          <div className={classes.loadingFont}>
            <div className={classes.loadingIcon}>⏳</div>
            Loading folder...
          </div>
        )}

        {!loading && error && <div className={classes.error}>⚠️ {error}</div>}

        {!loading && !error && items.length === 0 && (
          <div className={classes.fallbackStyle}>
            <p className={classes.previewUnableText}>This folder is empty.</p>
          </div>
        )}

        {!loading && !error && items.length > 0 && (
          
          <div className={classes.fallbackStyle} role="group" aria-label="Items">
            <div
              className={classes.folderItemsList}
              style={{ gap: 0 }} 
            >
              {items.map((item, index) => (
                <button
                  key={`${item.path}-${index}`}
                  onClick={() => handleItemClick(item)}
                  
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    width: '100%',
                    background: 'transparent',
                    border: 'none',
                    padding: '10px 0',
                    textAlign: 'left',
                    cursor: 'pointer',
                  }}
                  aria-label={item.name}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleItemClick(item);
                    }
                  }}
                >
                  <span className={classes.folderItemIcon}>
                    {getFileIcon(item.type)}
                  </span>
                  <span className={classes.folderItemName}>{item.name}</span>
                  
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FolderViewer;
